import api from "app/api";
import useExcelUrl from "app/useExcelUrl";
import { globalMessageHandler } from "common";
import useSettings from "features/settings/useSettings";
import moment from "moment";
import { createContext, useContext, useEffect, useState } from "react";

export type SalesReportData = {
  amount: number;
  boat: string;
  canceled_amount: number;
  admin_fee_amount: number;
  canceled_count: number;
  closed_event_count: number;
  program_closed_count: number;
  dynamic_price_diff: number;
  canceled_guests: number;
  refund_amount: number;
  refund_guests: number;
  count: number;
  email: string;
  guests: number;
  name: string;
  partner: string;
  partner_group: string;
  program: string;
  program_variant: string;
  t: string; // This represents a date, but in the example, it's a string
};

export type SalesReportDataFilterType = {
  from: string; //date string like "2024-01-01"
  to: string; //date string like "2024-01-01"
  boatIds?: number[];
  programIds?: number[];
  currency?: null | "EUR" | "HUF";
  partnerIds?: number[]; // Array of partner IDs
  partnerGroupIds?: number[]; // Array of partner group IDs
  sourceIds?: number[]; // Array of source IDs
  isGroup?: boolean | null;
};

export type SalesReportDataContextType = {
  data: SalesReportData[];
  filter: SalesReportDataFilterType;
  setFilter: (filter: SalesReportDataFilterType) => void;
  load: () => Promise<void>;
  loading: boolean;
  excelUrl?: string;
  excelWithBooking?: boolean;
  setExcelWithBooking: (excelWithBooking: boolean) => void;
};

export const SalesReportDataContext =
  createContext<SalesReportDataContextType | null>(null);

export default function useSalesReportData() {
  const [filter, setFilter] = useState<SalesReportDataFilterType>({
    from: moment().startOf("week").format("YYYY-MM-DD"),
    to: moment().endOf("week").format("YYYY-MM-DD"),
  });
  const [loading, setLoading] = useState(false);
  const [excelWithBooking, setExcelWithBooking] = useState(true);
  const [data, setData] = useState<SalesReportData[]>([]);
  const [settings] = useSettings({ key: "salesReport" });

  async function load() {
    //api is an axios instance
    setLoading(true);
    const { success, data, error } = await api.get("reports/advanced", {
      params: {
        ...filter,
        currency: settings.currency,
        dateType: settings.dateType,
        no_admin_fee: 1,
      },
    });
    setLoading(false);
    if (success) {
      setData(data);
    } else {
      globalMessageHandler.alert({
        severity: "error",
        message: typeof error === "string" ? error : JSON.stringify(error),
      });
    }
  }

  const excelUrl = useExcelUrl(
    "reports",
    null,
    {
      currency: settings.currency,
      ...settings,
      ...filter,
      value: settings.value === "admin_fee_amount" ? "amount" : settings.value,
      bookings: excelWithBooking ? 1 : 0,
      no_admin_fee: 1,
    },
    false,
  );

  useEffect(() => {
    load();
  }, [
    filter.programIds,
    filter.boatIds,
    filter.from,
    filter.to,
    filter.partnerIds,
    filter.partnerGroupIds,
    filter.sourceIds,
    filter.isGroup,
    settings.currency,
    settings.dateType,
  ]);
  return {
    data,
    filter,
    setFilter,
    load,
    loading,
    excelUrl,
    excelWithBooking,
    setExcelWithBooking,
  };
}

export function useSalesReportDataContext() {
  const context = useContext(SalesReportDataContext);
  if (!context) {
    throw Error(
      "useSalesReportDataContext cannot be used outside of SalesReportDataProvider.",
    );
  }
  return context;
}
